import { useState } from 'react'
import {
  Box,
  Grid,
  TextField,
  FormControlLabel,
  Switch,
  Divider,
  Typography,
} from '@material-ui/core'
import { DatePicker } from '@material-ui/pickers'
import {
  Calendar as CalendarIcon,
  Copy as CopyIcon,
  DollarSign as DollarSignIcon,
  FileText as FileTextIcon,
} from 'react-feather'
import { Controller, useForm } from 'react-hook-form'
import moment from 'moment'

import useSnackbar from 'hooks/useSnackbar'

import { BaseDialog, LoadingFeedback } from 'components'

import helpers from 'helpers'
import useStyles from './styles'
import * as service from 'service'
import schema from './schema'

const CheckoutDialog = ({
  open,
  setOpen,
  paymentOrder,
  paymentContract,
  refresh,
}) => {
  const snackbar = useSnackbar()
  const classes = useStyles()
  const [loading, setLoading] = useState(false)
  const [linkData, setLinkData] = useState(null)

  const { control, errors, handleSubmit } = useForm({
    validationSchema: schema,
    defaultValues: {
      expirationDate:
        paymentOrder?.expirationDate ||
        moment().add(1, 'day').format('YYYY-MM-DD'),
      orderReferenceDate:
        paymentOrder?.orderReferenceDate || moment().format('YYYY-MM'),
      nfeNumber: null,
      price: helpers.formatters.currencyInput(
        paymentOrder?.price || paymentContract?.price || '',
      ),
      sendEmail: false,
    },
  })

  const onSubmit = async (data) => {
    setLoading(true)
    try {
      if (paymentContract?.id) {
        const response = await service.dponet.paymentLink.create({
          paymentLink: {
            ...data,
            expirationDate: moment(data.expirationDate).format('YYYY-MM-DD'),
            orderReferenceDate: moment(data.orderReferenceDate).format(
              'YYYY-MM-DD',
            ),
            price: helpers.formatters.currencyOutput(data.price),
            paymentContractId: paymentContract.id,
          },
        })
        setLinkData(response?.data?.paymentOrder?.linkPayment)

        snackbar.open({
          message: 'Link de pagamento gerado com sucesso!',
          variant: 'success',
        })
      }

      if (paymentOrder?.id) {
        await service.dponet.paymentOrders.edit({
          paymentOrderId: paymentOrder.id,
          paymentOrder: {
            expirationDate: moment(data.expirationDate).format('YYYY-MM-DD'),
            orderReferenceDate: moment(data.orderReferenceDate).format(
              'YYYY-MM-DD',
            ),
            price: helpers.formatters.currencyOutput(data.price),
          },
        })

        snackbar.open({
          message: 'Link de pagamento atualizado com sucesso!',
          variant: 'success',
        })
      }
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(
          error?.response?.data?.error || 'Erro ao gerar link de pagamento',
        ),
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const copyLink = paymentOrder?.linkPayment || linkData

  const handleCopy = () => {
    window.navigator.clipboard.writeText(copyLink)
    snackbar.open({
      message: 'link de pagamento copiada com sucesso!',
      variant: 'success',
    })
  }

  const handleClose = (isOpen) => {
    setOpen(isOpen)
    refresh()
  }

  return (
    <BaseDialog
      open={open}
      title="Link de pagamento"
      actionButtonText={
        paymentOrder?.id
          ? 'Editar link de pagamento'
          : copyLink
          ? null
          : 'Gerar link de pagamento'
      }
      closeButtonText="Cancelar"
      setOpen={handleClose}
      formRef="id-new-link-payment"
      dialogSize="lg"
    >
      <LoadingFeedback open={loading} />
      <form onSubmit={handleSubmit(onSubmit)} id="id-new-link-payment">
        <Grid container spacing={2} style={{ margin: 0 }}>
          <Grid item xs={12} md={6}>
            <Controller
              as={
                <DatePicker
                  label="Vencimento *"
                  color="primary"
                  format="DD/MM/yyyy"
                  variant="outlined"
                  error={!!errors?.expirationDate}
                  helperText={errors?.expirationDate?.message}
                  fullWidth
                  disablePast
                  onChange={(newValue) => ({ value: newValue })}
                  InputProps={{
                    endAdornment: (
                      <Box onClick={(e) => e.stopPropagation()}>23:59:59</Box>
                    ),
                    startAdornment: (
                      <Box mr={2}>
                        <CalendarIcon />
                      </Box>
                    ),
                  }}
                />
              }
              control={control}
              name="expirationDate"
              mode="onChange"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              as={
                <DatePicker
                  label="Data de referência *"
                  color="primary"
                  format="MM/yyyy"
                  variant="dialog"
                  views={['year', 'month']}
                  error={!!errors?.orderReferenceDate}
                  helperText={errors?.orderReferenceDate?.message}
                  fullWidth
                  onChange={(newValue) => ({ value: newValue })}
                  InputProps={{
                    startAdornment: (
                      <Box mr={2}>
                        <CalendarIcon />
                      </Box>
                    ),
                  }}
                />
              }
              control={control}
              name="orderReferenceDate"
              mode="onChange"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              as={
                <TextField
                  disabled={paymentOrder?.id}
                  fullWidth
                  label="Digite o número da nota fiscal (optional)"
                  type="number"
                  error={!!errors.nfeNumber}
                  helperText={errors?.nfeNumber?.message}
                  InputProps={{
                    startAdornment: (
                      <Box mr={2}>
                        <FileTextIcon />
                      </Box>
                    ),
                  }}
                />
              }
              control={control}
              name="nfeNumber"
              mode="onChange"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              as={
                <TextField
                  fullWidth
                  label="Digite a valor da cobrança *"
                  type="text"
                  error={!!errors.price}
                  helperText={errors?.price?.message}
                  InputProps={{
                    startAdornment: (
                      <Box mr={2}>
                        <DollarSignIcon />
                      </Box>
                    ),
                  }}
                />
              }
              control={control}
              name="price"
              mode="onChange"
              onChange={([event]) => {
                return helpers.formatters.currencyInput(event.target.value)
              }}
            />
          </Grid>
          {!copyLink && (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Controller
                    as={<Switch color="primary" />}
                    name="sendEmail"
                    control={control}
                    onChange={([event]) => event.target.checked}
                  />
                }
                label="Enviar e-mail para o cliente com link de pagamento?"
              />
            </Grid>
          )}
          {copyLink && (
            <Grid item xs={12}>
              <Divider />
              <Box mt={2}>
                <Typography variant="subtitle1">
                  Link de pagamento gerado:
                </Typography>
              </Box>
              <Box mt={1} mb={2}>
                <Typography
                  variant="body1"
                  className={classes.copyLinkContainer}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    {copyLink}
                    <Box className={classes.copyLink}>
                      <CopyIcon onClick={handleCopy} />
                    </Box>
                  </Box>
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </form>
    </BaseDialog>
  )
}

export default CheckoutDialog
